import { useState, useEffect } from 'react';
/* eslint-disable */
import PropTypes from 'prop-types';
import {
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    Button,
    TextField,
    MenuItem,
    Grid,
    Typography,
    Box,
    IconButton,
    InputAdornment
} from '@mui/material';
import { LoadingButton } from '@mui/lab';
import { CalendarMonthOutlined, AccountBalanceWalletOutlined, CloseOutlined, PaymentsOutlined } from '@mui/icons-material';
import { storeSalary } from 'utils/API/Mis Apis/HrPortelApis';
import { handleCatchError } from 'utils/CatchHandler';
import { globalNotifySuccess } from 'views/utilities/AlertToast';

const SalaryPaymentModal = ({ open, handleClose, employee, onSubmit, editData, onRefresh }) => {
    const currentYear = new Date().getFullYear();
    const currentMonth = new Date().getMonth() + 1; // 1-12
    const [year, setYear] = useState(currentYear);
    const [month, setMonth] = useState(currentMonth);
    const [amount, setAmount] = useState('');
    const [loading, setLoading] = useState(false);
    const [errors, setErrors] = useState({});
    // Generate years (current year - 5 to current year + 1)
    const years = Array.from({ length: 7 }, (_, i) => currentYear - 5 + i);

    const months = [
        { value: 1, label: 'January' },
        { value: 2, label: 'February' },
        { value: 3, label: 'March' },
        { value: 4, label: 'April' },
        { value: 5, label: 'May' },
        { value: 6, label: 'June' },
        { value: 7, label: 'July' },
        { value: 8, label: 'August' },
        { value: 9, label: 'September' },
        { value: 10, label: 'October' },
        { value: 11, label: 'November' },
        { value: 12, label: 'December' }
    ];

    useEffect(() => {
        if (open) {
            if (editData) {
                setYear(Number(editData.year));
                setMonth(Number(editData.month));
                setAmount(editData.amount || '');
            } else {
                setYear(currentYear);
                setMonth(currentMonth);
                setAmount('');
            }
            setErrors({});
        }
    }, [open, editData, currentYear, currentMonth]);

    const validate = () => {
        let tempErrors = {};
        if (!amount) tempErrors.amount = 'Amount is required';
        else if (amount <= 0) tempErrors.amount = 'Amount must be greater than 0';

        setErrors(tempErrors);
        return Object.keys(tempErrors).length === 0;
    };

    const handleSubmit = async () => {
        if (!validate()) return;

        setLoading(true);
        const data = {
            employee_id: employee?.id,
            year,
            month,
            amount: Number(amount)
        };

        if (editData?.id) {
            data.id = editData.id;
        }

        try {
            const response = await storeSalary(data);
            if (response?.data?.success) {
                globalNotifySuccess(editData ? 'Salary record updated successfully' : 'Salary marked as paid successfully');
                if (onRefresh) onRefresh();
                if (onSubmit) onSubmit({ employeeName: employee?.name });
                setAmount('');
                setYear(currentYear);
                setMonth(currentMonth);
                handleClose();
            }
        } catch (error) {
            console.error('Error submitting salary:', error);
            handleCatchError(error);
        } finally {
            setLoading(false);
        }
    };

    return (
        <Dialog
            open={open}
            onClose={handleClose}
            maxWidth="xs"
            fullWidth
            PaperProps={{
                sx: {
                    borderRadius: 3,
                    boxShadow: '0 20px 40px rgba(0,0,0,0.1)',
                    overflow: 'hidden'
                }
            }}
        >
            <DialogTitle sx={{ p: 0, pb: 4 }}>
                <Box
                    sx={{
                        background: 'linear-gradient(135deg, #1e3a8a 0%, #1976d2 100%)',
                        px: 3,
                        py: 2.5,
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'space-between',
                        color: '#fff'
                    }}
                >
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
                        <Box
                            sx={{
                                width: 40,
                                height: 40,
                                borderRadius: 1.5,
                                bgcolor: 'rgba(255,255,255,0.15)',
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'center'
                            }}
                        >
                            <PaymentsOutlined sx={{ color: '#fff' }} />
                        </Box>
                        <Box>
                            <Typography color="#fff" variant="h6" fontWeight={700} lineHeight={1.2}>
                                Salary {editData ? 'Update' : 'Payment'}
                            </Typography>
                            {employee && (
                                <Typography variant="caption" sx={{ color: 'rgba(255,255,255,0.8)' }}>
                                    {employee.name} • {employee.id}
                                </Typography>
                            )}
                        </Box>
                    </Box>
                    <IconButton
                        size="small"
                        onClick={handleClose}
                        sx={{ color: 'rgba(255,255,255,0.8)', '&:hover': { color: '#fff', bgcolor: 'rgba(255,255,255,0.1)' } }}
                    >
                        <CloseOutlined fontSize="small" />
                    </IconButton>
                </Box>
            </DialogTitle>

            <DialogContent sx={{ pt: 3, pb: 2 }}>
                <Grid container spacing={3}>
                    <Grid item xs={6}>
                        <TextField
                            select
                            label="Year"
                            fullWidth
                            sx={{ margin: '10px' }}
                            variant="outlined"
                            value={year}
                            onChange={(e) => setYear(e.target.value)}
                            size="small"
                            InputProps={{
                                startAdornment: (
                                    <InputAdornment position="start">
                                        <CalendarMonthOutlined sx={{ fontSize: 18, color: 'text.secondary', opacity: 0.7 }} />
                                    </InputAdornment>
                                )
                            }}
                        >
                            {years.map((y) => (
                                <MenuItem key={y} value={y}>
                                    {y}
                                </MenuItem>
                            ))}
                        </TextField>
                    </Grid>
                    <Grid item xs={6}>
                        <TextField
                            select
                            label="Month"
                            fullWidth
                            sx={{ margin: '10px' }}
                            variant="outlined"
                            value={month}
                            onChange={(e) => setMonth(e.target.value)}
                            size="small"
                            InputProps={{
                                startAdornment: (
                                    <InputAdornment position="start">
                                        <CalendarMonthOutlined sx={{ fontSize: 18, color: 'text.secondary', opacity: 0.7 }} />
                                    </InputAdornment>
                                )
                            }}
                        >
                            {months.map((m) => (
                                <MenuItem key={m.value} value={m.value}>
                                    {m.label}
                                </MenuItem>
                            ))}
                        </TextField>
                    </Grid>
                    <Grid item xs={12}>
                        <TextField
                            label="Amount"
                            fullWidth
                            type="number"
                            value={amount}
                            onChange={(e) => setAmount(e.target.value)}
                            error={!!errors.amount}
                            helperText={errors.amount}
                            sx={{
                                '& .MuiOutlinedInput-root': {
                                    borderRadius: 2,
                                    bgcolor: '#f8fafc'
                                }
                            }}
                            InputProps={{
                                startAdornment: (
                                    <InputAdornment position="start">
                                        <AccountBalanceWalletOutlined sx={{ color: '#1976d2' }} />
                                    </InputAdornment>
                                ),
                                endAdornment: (
                                    <InputAdornment position="end">
                                        <Typography sx={{ color: 'text.secondary', fontWeight: 600 }}>INR</Typography>
                                    </InputAdornment>
                                )
                            }}
                            placeholder="Enter salary amount"
                        />
                    </Grid>
                </Grid>
            </DialogContent>

            <DialogActions sx={{ p: 3, gap: 1.5 }}>
                <Button
                    onClick={handleClose}
                    variant="text"
                    sx={{
                        color: 'text.secondary',
                        fontWeight: 600,
                        textTransform: 'none',
                        '&:hover': { bgcolor: '#f1f5f9' }
                    }}
                >
                    Cancel
                </Button>
                <LoadingButton
                    onClick={handleSubmit}
                    variant="contained"
                    loading={loading}
                    sx={{
                        px: 4,
                        py: 1,
                        borderRadius: 2,
                        textTransform: 'none',
                        fontWeight: 700,
                        background: 'linear-gradient(135deg, #1e3a8a 0%, #1976d2 100%)',
                        boxShadow: '0 4px 14px rgba(25, 118, 210, 0.3)',
                        '&:hover': {
                            background: 'linear-gradient(135deg, #1e40af 0%, #1d4ed8 100%)',
                            boxShadow: '0 6px 20px rgba(25, 118, 210, 0.4)'
                        }
                    }}
                >
                    {editData ? 'Update Salary' : 'Mark Paid'}
                </LoadingButton>
            </DialogActions>
        </Dialog>
    );
};

SalaryPaymentModal.propTypes = {
    open: PropTypes.bool.isRequired,
    handleClose: PropTypes.func.isRequired,
    employee: PropTypes.object,
    onSubmit: PropTypes.func.isRequired,
    editData: PropTypes.object,
    onRefresh: PropTypes.func
};

export default SalaryPaymentModal;
